import { useCallback, useEffect, useMemo, useReducer } from "react";
import toast from "react-hot-toast";
import {
  useLazyGetDomainDataQuery,
  useUpdateOrderStatusMutation,
  useUpdateBulkOrderStatusMutation,
} from "services/dfy-service";
import { useDebounce } from "./usedebounce";
import { DEFAULT_ROWS_PER_PAGE } from "../constants/orderConstants";

// Action types
const ACTIONS = {
  SET_ORDERS: "SET_ORDERS",
  SET_SEARCH: "SET_SEARCH",
  SET_STATUS: "SET_STATUS",
  SET_PAGE: "SET_PAGE",
  SET_ROWS_PER_PAGE: "SET_ROWS_PER_PAGE",
  TOGGLE_CONTACT_SELECTION: "TOGGLE_CONTACT_SELECTION",
  SELECT_ALL_CONTACTS: "SELECT_ALL_CONTACTS",
  CLEAR_SELECTION: "CLEAR_SELECTION",
  RESET: "RESET",
  UPDATE_SINGLE_CONTACT: "UPDATE_SINGLE_CONTACT",
  UPDATE_BULK_CONTACTS: "UPDATE_BULK_CONTACTS",
  SET_DATA_MODIFIED: "SET_DATA_MODIFIED",
};

// Initial state
const initialState = {
  orders: null,
  totalCount: 0,
  searchText: "",
  status: "",
  page: 0,
  rowsPerPage: DEFAULT_ROWS_PER_PAGE,
  selectedContacts: [],
  dataModified: false,
};

// Reducer function
const orderDetailsReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.SET_ORDERS:
      return {
        ...state,
        orders: action.payload.data,
        totalCount: action.payload.total,
      };

    case ACTIONS.SET_SEARCH:
      return { ...state, searchText: action.payload };

    case ACTIONS.SET_STATUS:
      return { ...state, status: action.payload };

    case ACTIONS.SET_PAGE:
      return { ...state, page: action.payload };

    case ACTIONS.SET_ROWS_PER_PAGE:
      return { ...state, rowsPerPage: action.payload, page: 0 };

    case ACTIONS.TOGGLE_CONTACT_SELECTION: {
      const contact = action.payload;
      const exists = state.selectedContacts.some((c) => c._id === contact._id);
      return {
        ...state,
        selectedContacts: exists
          ? state.selectedContacts.filter((c) => c._id !== contact._id)
          : [...state.selectedContacts, contact],
      };
    }

    case ACTIONS.SELECT_ALL_CONTACTS:
      return { ...state, selectedContacts: action.payload };

    case ACTIONS.CLEAR_SELECTION:
      return { ...state, selectedContacts: [] };

    case ACTIONS.RESET:
      return { ...initialState };

    case ACTIONS.UPDATE_SINGLE_CONTACT: {
      const { contactId, newStatus } = action.payload;
      if (!state.orders?.domains) return state;

      const updatedDomains = state.orders.domains.map((domain) => ({
        ...domain,
        contactDetails: domain.contactDetails?.map((contact) =>
          contact._id === contactId ? { ...contact, status: newStatus } : contact
        ),
      }));

      return {
        ...state,
        orders: { ...state.orders, domains: updatedDomains },
        dataModified: true,
      };
    }

    case ACTIONS.UPDATE_BULK_CONTACTS: {
      const { contactIds, newStatus } = action.payload;
      if (!state.orders?.domains) return state;

      const updatedDomains = state.orders.domains.map((domain) => ({
        ...domain,
        contactDetails: domain.contactDetails?.map((contact) =>
          contactIds.includes(contact._id)
            ? { ...contact, status: newStatus }
            : contact
        ),
      }));

      return {
        ...state,
        orders: { ...state.orders, domains: updatedDomains },
        selectedContacts: [],
        dataModified: true,
      };
    }

    case ACTIONS.SET_DATA_MODIFIED:
      return { ...state, dataModified: action.payload };

    default:
      return state;
  }
};

/**
 * Custom hook for managing order details state
 * @param {Object} options - Hook options
 * @param {string} options.orderId - Order ID to fetch
 */
export const useOrderDetails = ({ orderId }) => {
  const [state, dispatch] = useReducer(orderDetailsReducer, initialState);
  const debouncedSearchText = useDebounce(state.searchText);

  // RTK Query hooks
  const [
    fetchDomainData,
    { data: queryData, isLoading, isFetching, error },
  ] = useLazyGetDomainDataQuery();

  const [updateStatusMutation] = useUpdateOrderStatusMutation();
  const [updateBulkStatusMutation] = useUpdateBulkOrderStatusMutation();

  // Get all contacts from domains
  const allContacts = useMemo(() => {
    if (!state.orders?.domains) return [];
    return state.orders.domains.flatMap((domain) => domain.contactDetails || []);
  }, [state.orders?.domains]);

  // Update local state when query data changes
  useEffect(() => {
    if (queryData) {
      dispatch({
        type: ACTIONS.SET_ORDERS,
        payload: { data: queryData.data, total: queryData?.data?.pagination?.total },
      });
    }
  }, [queryData]);

  // Fetch order details
  const fetchOrderDetails = useCallback(async () => {
    if (!orderId) return;

    fetchDomainData({
      orderId,
      status: state.status !== "all" ? state.status : "",
      query: debouncedSearchText,
      page: state.page + 1,
      limit: state.rowsPerPage,
    });
  }, [
    orderId,
    state.status,
    debouncedSearchText,
    state.page,
    state.rowsPerPage,
    fetchDomainData,
  ]);

  // Update single order status
  const updateOrderStatus = useCallback(
    async (status, contact) => {
      try {
        const response = await updateStatusMutation({
          orderId,
          email: contact.email,
          contactId: contact._id,
          status,
        }).unwrap();

        if (response.status) {
          toast.success("Status Updated Successfully");
          // Optimistically update local state
          dispatch({
            type: ACTIONS.UPDATE_SINGLE_CONTACT,
            payload: { contactId: contact._id, newStatus: status },
          });
        }
      } catch (error) {
        toast.error("Failed to update status");
        console.error(error);
        // Refetch on error to reset to correct state
        fetchOrderDetails();
      }
    },
    [orderId, fetchOrderDetails, updateStatusMutation]
  );

  // Update bulk order status
  const updateBulkOrderStatus = useCallback(
    async (status) => {
      if (state.selectedContacts.length === 0) return;

      const contactIds = state.selectedContacts.map((c) => c._id);

      try {
        const response = await updateBulkStatusMutation({
          orders: state.selectedContacts,
          status,
        }).unwrap();

        if (response.status) {
          toast.success("Status Updated Successfully");
          // Optimistically update local state
          dispatch({
            type: ACTIONS.UPDATE_BULK_CONTACTS,
            payload: { contactIds, newStatus: status },
          });
        }
      } catch (error) {
        toast.error("Failed to update status");
        console.error(error);
        // Refetch on error to reset to correct state
        fetchOrderDetails();
      }
    },
    [state.selectedContacts, fetchOrderDetails, updateBulkStatusMutation]
  );

  // Fetch orders when dependencies change
  useEffect(() => {
    fetchOrderDetails();
  }, [fetchOrderDetails]);

  // Action handlers
  const handleSearchChange = useCallback((e) => {
    dispatch({ type: ACTIONS.SET_SEARCH, payload: e.target.value.toLowerCase() });
  }, []);

  const handleStatusChange = useCallback((value) => {
    dispatch({ type: ACTIONS.SET_STATUS, payload: value });
  }, []);

  const handlePageChange = useCallback((_, newPage) => {
    dispatch({ type: ACTIONS.SET_PAGE, payload: newPage });
  }, []);

  const handleRowsPerPageChange = useCallback((e) => {
    dispatch({ type: ACTIONS.SET_ROWS_PER_PAGE, payload: e.target.value });
  }, []);

  const handleToggleContact = useCallback((contact) => {
    dispatch({ type: ACTIONS.TOGGLE_CONTACT_SELECTION, payload: contact });
  }, []);

  const handleSelectAll = useCallback(
    (event) => {
      if (event.target.checked) {
        dispatch({ type: ACTIONS.SELECT_ALL_CONTACTS, payload: allContacts });
      } else {
        dispatch({ type: ACTIONS.CLEAR_SELECTION });
      }
    },
    [allContacts]
  );

  const isContactSelected = useCallback(
    (contact) => state.selectedContacts.some((c) => c._id === contact._id),
    [state.selectedContacts]
  );

  const reset = useCallback(() => {
    dispatch({ type: ACTIONS.RESET });
  }, []);

  const setDataModified = useCallback((modified) => {
    dispatch({ type: ACTIONS.SET_DATA_MODIFIED, payload: modified });
  }, []);

  return {
    ...state,
    isLoading: isLoading || isFetching,
    error,
    debouncedSearchText,
    allContacts,
    selectedCount: state.selectedContacts.length,
    handleSearchChange,
    handleStatusChange,
    handlePageChange,
    handleRowsPerPageChange,
    handleToggleContact,
    handleSelectAll,
    isContactSelected,
    updateOrderStatus,
    updateBulkOrderStatus,
    reset,
    refetch: fetchOrderDetails,
    setDataModified,
  };
};

export default useOrderDetails;
